import { EPixelEventName } from '@/constants/pixel';
import useGetDiagnosisCategories from '@/hooks/telemedicine/useGetDiagnosisCategories';
import useSearchDoctorsByUser from '@/hooks/telemedicine/useSearchDoctorsByUser';
import { DiagnosisCategoryInfoModel, SeoCategory } from '@/models/diagnosis/category';
import { NonF2fDoctor, TelemedicinePageQueries } from '@/models/doctor/telemedicine';
import { ECategoryType } from '@/services/doctor/DiagnosisCategoryService';
import { BreadCrumb, ROUTES } from '@/types/common';
import Pixel from '@/utils/pixel/pixel';
import { addQueryString } from '@/utils/url';
import { useRouter } from 'next/router';
import { useEffect, useMemo } from 'react';

export interface TelemedicineDoctorsViewModel {
  categoriesInfo: DiagnosisCategoryInfoModel;
  nonF2fDoctors: NonF2fDoctor[];
  breadCrumbs: BreadCrumb[];
  category: SeoCategory;
  queries: TelemedicinePageQueries;
  currentPage: number;
  totalPage: number;
  categoryType: ECategoryType;
  categoryId: string;
}

const defaultSeoCategory: SeoCategory = {
  id: '',
  name: '',
  getSeoTitle: () => '비대면 진료 | 진료과·질환별 원격진료 - 닥터나우',
  getSeoDescription: () => '내 증상에 맞는 의사를 빠르게 찾고, 병원 방문 없이 비대면 진료로 약 처방까지 받아보세요.',
  getKeywords: () => '비대면진료, 원격진료, 온라인진료, 비대면처방, 약처방, 약배송, 약배달, 진료비',
};

const useTelemedicineDoctorsViewModel = (): TelemedicineDoctorsViewModel => {
  const router = useRouter();

  const queries = router.query as unknown as TelemedicinePageQueries;
  const { type, id, page = '1' } = queries;

  const currentPage = Number(page);

  const breadCrumbs: BreadCrumb[] = [
    {
      url: ROUTES.TELEMEDICINE.DOCTORS.path + addQueryString(queries),
      label: '비대면 진료',
      logName: 'telemedicine_doctors',
    },
  ];

  const { categoriesInfo } = useGetDiagnosisCategories();
  const { nonF2fDoctors, totalPage } = useSearchDoctorsByUser(type, id, currentPage);

  const seoCategory = useMemo(() => {
    if (type === ECategoryType.Treatment) {
      return categoriesInfo.findTreatmentByTreatmentId(id) ?? defaultSeoCategory;
    }
    return categoriesInfo.findDepartmentCategoryByCategoryId(id) ?? defaultSeoCategory;
  }, [categoriesInfo, type, id]);

  useEffect(() => {
    if (!seoCategory) {
      return;
    }

    Pixel.trackCustom(EPixelEventName.PageIn, {
      ...(type === ECategoryType.Treatment ? { symptom: seoCategory.name } : { subject: seoCategory.name }),
    });
  }, [seoCategory]);

  return {
    currentPage,
    queries,
    category: seoCategory,
    categoriesInfo,
    nonF2fDoctors,
    breadCrumbs,
    totalPage,
    categoryType: type,
    categoryId: id,
  };
};

export default useTelemedicineDoctorsViewModel;
